import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { fetchVendorMonthlyReport } from '../supabaseClient';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { FaTachometerAlt, FaSignOutAlt, FaBars, FaTimes, FaSearch, FaChevronLeft, FaChevronRight, FaCog, FaFileAlt, FaChartBar, FaUsers, FaTicketAlt } from 'react-icons/fa';
import OrganizationSettingsTab from '../components/tabs/OrganizationSettingsTab';
import OrganizationReportsTab from '../components/tabs/OrganizationReportsTab';
import OrganizationUsersTab from '../components/tabs/OrganizationUsersTab';
import OrganizationTicketsTab from '../components/tabs/OrganizationTicketsTab';
import { fetchPublicOrganizationById, fetchOrganizationVendorsWithData } from '../apiClient';
import { supabase } from '../supabaseClient';

// Add StatusIndicator component
const StatusIndicator = ({ status }) => {
  switch (status) {
    case 'active':
      return <span className="text-green-500">🟢 Active</span>;
    case 'upcoming':
      return <span className="text-orange-500">🟠 Upcoming Events</span>;
    default:
      return <span className="text-red-500">🔴 No Events</span>;
  }
};

function OrganizationDashboard() {
  const { organizationId } = useParams(); // Get organizationId from URL
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview'); // Default tab is overview
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const vendorsPerPage = 6; // Show 6 vendors per page (2 rows of 3 in desktop view)
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [organizationData, setOrganizationData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    endDate: new Date()
  });
  const [generatingReport, setGeneratingReport] = useState(false);

  useEffect(() => {
    const fetchOrganizationData = async () => {
      const { data, error } = await fetchPublicOrganizationById(organizationId);
      if (error) throw error;
      setOrganizationData(data);
    };
    fetchOrganizationData();
    
    const fetchVendors = async () => {
      try {
        if (!organizationId) {
          throw new Error('No organization ID found');
        }

        // Get current month's start and end dates
        const now = new Date();
        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
        const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString();

        // Get vendors basic info with filtered events and tickets
        const { data: vendorsData, error: vendorsError } = await fetchOrganizationVendorsWithData(organizationId);

        if (vendorsError) throw vendorsError;

        // Process the data for each vendor
        const processedVendors = vendorsData.map(vendor => {
          // Filter events for current month
          const currentMonthEvents = (vendor.events || []).filter(event => {
            const eventDate = new Date(event.start_date);
            return eventDate >= new Date(startOfMonth) && eventDate <= new Date(endOfMonth);
          });

          // Filter tickets for current month
          const currentMonthTickets = (vendor.tickets_sold || []).filter(ticket => {
            const purchaseDate = new Date(ticket.purchase_date);
            return purchaseDate >= new Date(startOfMonth) && purchaseDate <= new Date(endOfMonth);
          });
          
          // Calculate metrics for current month only
          const totalEvents = currentMonthEvents.length;
          const totalTickets = currentMonthTickets.length;
          const totalRevenue = currentMonthTickets.reduce((sum, ticket) => sum + (ticket.price_paid || 0), 0);
          const avgAttendance = totalEvents > 0 ? Math.round(totalTickets / totalEvents) : 0;
          
          // Get next upcoming event (can be beyond current month)
          const upcomingEvents = (vendor.events || [])
            .filter(event => new Date(event.start_date) > new Date())
            .sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
          
          const nextEvent = upcomingEvents[0];
          
          // Determine status based on all events (not just current month)
          let status = 'inactive';
          if (upcomingEvents.length > 0) {
            status = 'active';
          }

          return {
            ...vendor,
            totalEvents,
            totalTickets,
            totalRevenue,
            avgAttendance,
            nextEvent,
            status
          };
        });

        setVendors(processedVendors);
      } catch (err) {
        console.error('Error fetching vendors:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchVendors();
  }, [organizationId]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/vendor-login');
    window.location.reload();
  };

  const NavItem = ({ icon: Icon, label, tabName }) => (
    <button
      onClick={() => setActiveTab(tabName)}
      className={`flex items-center w-full px-6 py-4 text-left transition-colors duration-200 ${
        activeTab === tabName
          ? "bg-blue-600 text-white"
          : "text-gray-600 hover:bg-gray-100"
      }`}
    >
      <Icon className={`w-5 h-5 mr-3 ${
        activeTab === tabName ? "text-white" : "text-gray-500"
      }`} />
      <span className="font-medium">{label}</span>
    </button>
  );

  // Filter vendors based on search term
  const filteredVendors = vendors.filter(vendor => 
    vendor.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    vendor.type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate pagination
  const totalPages = Math.ceil(filteredVendors.length / vendorsPerPage);
  const indexOfLastVendor = currentPage * vendorsPerPage;
  const indexOfFirstVendor = indexOfLastVendor - vendorsPerPage;
  const currentVendors = filteredVendors.slice(indexOfFirstVendor, indexOfLastVendor);

  // Reset to first page when searching
  useEffect(() => {
    setCurrentPage(1);
    //console.log('vendors', currentVendors);
    //console.log('vendors', vendors);
  }, [searchTerm]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    // Scroll to top of vendor list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex justify-center items-center mt-6 space-x-2">
        <button
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-3 py-1 rounded-md ${
            currentPage === 1
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FaChevronLeft className="w-4 h-4" />
        </button>

        {/* Page numbers */}
        <div className="flex space-x-1">
          {[...Array(totalPages)].map((_, index) => {
            const pageNumber = index + 1;
            // Show current page, first page, last page, and pages around current
            if (
              pageNumber === 1 ||
              pageNumber === totalPages ||
              (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
            ) {
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-3 py-1 rounded-md ${
                    currentPage === pageNumber
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            } else if (
              pageNumber === currentPage - 2 ||
              pageNumber === currentPage + 2
            ) {
              // Show ellipsis
              return <span key={pageNumber}>...</span>;
            }
            return null;
          })}
        </div>

        <button
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-3 py-1 rounded-md ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FaChevronRight className="w-4 h-4" />
        </button>
      </div>
    );
  };


  const generateVendorReport = async (vendor) => {
    try {
      const { data, error } = await fetchVendorMonthlyReport(vendor.id);
      if (error) throw error;
      
      // Format currency
      const formatCurrency = (amount) => `$${amount.toFixed(2)}`;
      
      // Create a printable document
      const reportHTML = `
        <html>
          <head>
            <title>Monthly Report - ${vendor.company_name}</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 30px; }
              h1, h2, h3 { color: #333; }
              .header { margin-bottom: 20px; border-bottom: 1px solid #eee; padding-bottom: 10px; }
              .metrics-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 15px; margin-bottom: 25px; }
              .metric-card { background-color: #f9f9f9; padding: 15px; border-radius: 5px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
              .metric-value { font-size: 24px; font-weight: bold; color: #2563eb; margin: 5px 0; }
              .metric-label { font-size: 14px; color: #666; }
              table { width: 100%; border-collapse: collapse; margin: 15px 0; }
              th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
              th { background-color: #f2f2f2; }
              .footer { margin-top: 30px; font-size: 12px; color: #666; }
              .section { margin-bottom: 25px; }
              .section-title { border-bottom: 1px solid #eee; padding-bottom: 5px; }
            </style>
          </head>
          <body>
            <div class="header">
              <h1>Monthly Report - ${vendor.company_name}</h1>
              <p>Period: ${new Date(data.summary.reportPeriod.start).toLocaleDateString()} - ${new Date(data.summary.reportPeriod.end).toLocaleDateString()}</p>
              <p>Generated on: ${new Date().toLocaleString()}</p>
            </div>
            
            <div class="section">
              <h2 class="section-title">Performance Overview</h2>
              <div class="metrics-grid">
                <div class="metric-card">
                  <div class="metric-label">Total Revenue</div>
                  <div class="metric-value">${formatCurrency(data.summary.totalRevenue)}</div>
                </div>
                
                <div class="metric-card">
                  <div class="metric-label">Tickets Sold</div>
                  <div class="metric-value">${data.summary.ticketCount}</div>
                </div>
                
                <div class="metric-card">
                  <div class="metric-label">Number of Events</div>
                  <div class="metric-value">${data.summary.eventCount}</div>
                </div>
                
                <div class="metric-card">
                  <div class="metric-label">Average Ticket Price</div>
                  <div class="metric-value">${formatCurrency(data.summary.avgTicketPrice)}</div>
                </div>
              </div>
            </div>
            
            <div class="section">
              <h2 class="section-title">Top Performing Events</h2>
              <table>
                <thead>
                  <tr>
                    <th>Event Name</th>
                    <th>Tickets Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  ${data.topEvents.map(event => `
                    <tr>
                      <td>${event.name}</td>
                      <td>${event.ticketsSold}</td>
                      <td>${formatCurrency(event.revenue)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="section">
              <h2 class="section-title">Performance by Event Type</h2>
              <table>
                <thead>
                  <tr>
                    <th>Event Type</th>
                    <th>Number of Events</th>
                    <th>Tickets Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  ${data.byEventType.map(type => `
                    <tr>
                      <td>${type.type || 'Uncategorized'}</td>
                      <td>${type.count}</td>
                      <td>${type.tickets}</td>
                      <td>${formatCurrency(type.revenue)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="section">
              <h2 class="section-title">Weekly Sales Trends</h2>
              <table>
                <thead>
                  <tr>
                    <th>Week</th>
                    <th>Tickets Sold</th>
                    <th>Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  ${data.weeklySales.map(week => `
                    <tr>
                      <td>${week.week}</td>
                      <td>${week.ticketsSold}</td>
                      <td>${formatCurrency(week.revenue)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
            
            <div class="footer">
              <p>All times are in your local timezone.</p>
            </div>
          </body>
        </html>
      `;
      
      // Open a new window and print
      const reportWindow = window.open('', '_blank');
      reportWindow.document.write(reportHTML);
      reportWindow.document.close();
      
      // Add a small delay before printing to ensure the content is loaded
      setTimeout(() => {
        reportWindow.print();
      }, 500);
      
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Failed to generate report: ' + error.message);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search vendors by name, contact, or type..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                  <FaSearch className="text-gray-400" />
                </div>
              </div>
              {searchTerm && (
                <p className="mt-2 text-sm text-gray-600">
                  Found {filteredVendors.length} vendor{filteredVendors.length !== 1 ? 's' : ''}
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentVendors.map(vendor => (
                <div key={vendor.id} className="bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow">
                  {/* Top Section - Identification */}
                  <div className="p-6 border-b">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center">
                        {vendor.logoURL ? (
                          <img 
                            src={vendor.logoURL} 
                            alt={`${vendor.company_name} logo`}
                            className="w-16 h-16 rounded-full object-cover mr-4"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center mr-4">
                            <span className="text-2xl font-bold text-gray-400">
                              {vendor.company_name?.charAt(0)?.toUpperCase() || 'S'}
                            </span>
                          </div>
                        )}
                        <div>
                          <h3 className="text-lg font-semibold">{vendor.company_name}</h3>
                          <p className="text-gray-500">{vendor.school_type || 'School'}</p>
                        </div>
                      </div>
                      <StatusIndicator status={vendor.status} />
                    </div>
                  </div>

                  {/* Middle Section - Metrics */}
                  <div className="p-6 border-b space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">Total Events</p>
                        <p className="text-lg font-bold text-gray-700">
                          {vendor.totalEvents} Events
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">Total Tickets</p>
                        <p className="text-lg font-bold text-gray-700">
                          {vendor.totalTickets.toLocaleString()} Sold
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">Revenue</p>
                        <p className="text-lg font-bold text-gray-700">
                          ${vendor.totalRevenue.toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm font-medium text-gray-500">Avg Attendance</p>
                        <p className="text-lg font-bold text-gray-700">
                          {vendor.avgAttendance.toLocaleString()} Fans/Event
                        </p>
                      </div>
                    </div>

                    {vendor.nextEvent && (
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <p className="text-sm font-medium text-blue-800">Next Event</p>
                        <p className="font-semibold text-blue-900">{vendor.nextEvent.title}</p>
                        <p className="text-sm text-blue-700">
                          {new Date(vendor.nextEvent.start_date).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-blue-600">{vendor.nextEvent.event_type}</p>
                      </div>
                    )}
                  </div>

                  {/* Bottom Section - Actions */}
                  <div className="p-6 space-y-3">
                    <button 
                      onClick={() => navigate(`/organization/${organizationId}/vendor/${vendor.id}`)}
                      className="w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      View Dashboard
                    </button>
                    <div className="grid grid-cols-2 gap-3">
                      <Link 
                        to={`/organization/${organizationId}/vendor/${vendor.id}?tab=events`} 
                        className="bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Manage Events
                      </Link>
                      <button 
                        onClick={() => generateVendorReport(vendor)}
                        className="bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                      >
                        Generate Reports
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredVendors.length === 0 ? (
              <div className="text-center text-gray-500 mt-8">
                {searchTerm 
                  ? `No vendors found matching "${searchTerm}"`
                  : 'No vendors found for this organization'
                }
              </div>
            ) : (
              renderPagination()
            )}
          </>
        );
      case 'reports':
        return <OrganizationReportsTab organizationId={organizationId} />;
      case 'tickets':
        return <OrganizationTicketsTab organizationId={organizationId} />;
      case 'settings':
        return <OrganizationSettingsTab organizationId={organizationId} />;
      case 'users':
        return <OrganizationUsersTab organizationId={organizationId} />;
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="p-8 text-center">Loading vendors...</div>;
  }

  if (error) {
    return <div className="p-8 text-center text-red-500">Error: {error}</div>;
  }

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed lg:static inset-y-0 left-0 w-64 bg-white shadow-lg transform ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 transition-transform duration-200 ease-in-out z-50`}
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b flex justify-center">
            <h1 className="text-2xl font-bold text-center text-gray-800">
              <div className="flex items-center justify-center">
                <div className="flex items-center justify-center">
                  <img src={organizationData?.logourl} alt="Organization Logo" className="w-10 h-10 rounded-full object-cover mr-4" /> 
                  <h1 className="text-2xl font-bold text-center text-gray-800">{organizationData?.company_name || 'Valley Tickets'}</h1> 
                </div>
              </div>
            </h1>
          </div>

          <nav className="flex-1 py-4">
            <NavItem
              icon={FaTachometerAlt}
              label="Overview"
              tabName="overview"
            />
            <NavItem
              icon={FaFileAlt}
              label="Reports"
              tabName="reports"
            />
            <NavItem
              icon={FaTicketAlt}
              label="Tickets"
              tabName="tickets"
            />
            <NavItem
              icon={FaUsers}
              label="Users"
              tabName="users"
            />
            <NavItem
              icon={FaCog}
              label="Settings"
              tabName="settings"
            />
          </nav>

          <button
            onClick={handleSignOut}
            className="flex items-center px-6 py-4 text-red-600 hover:bg-red-50 transition-colors duration-200"
          >
            <FaSignOutAlt className="w-5 h-5 mr-3" />
            <span className="font-medium">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="lg:hidden p-2 rounded-md hover:bg-gray-100"
                aria-label="Toggle Sidebar"
              >
                {isSidebarOpen ? (
                  <FaTimes className="w-6 h-6 text-gray-600" />
                ) : (
                  <FaBars className="w-6 h-6 text-gray-600" />
                )}
              </button>
              <h1 className="ml-4 text-xl font-semibold text-gray-800">
                {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
              </h1>
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden overflow-y-auto bg-gray-100 p-6">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default OrganizationDashboard; 